/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * WHO SUPPLIED EACH DOCUMENT (Invite with documents, approved 14/10/2026).
 * session_documents.supplied_via records the route a document came in by:
 *   client_portal    the client uploaded it through the portal (save-document)
 *   officer_upload   an officer uploaded it (the dashboard, or Invite with documents)
 *   migration_pack   it came in with a migrated client's pack (Migrate Client)
 * uploaded_by is the officer's user id on an officer upload; client_confirmed_at
 * is when the client confirmed a document the firm supplied is theirs and current.
 *
 * The columns arrive with migrations/2026-10-14_invite_with_documents.sql. Until
 * a database has them, a write naming them is refused by PostgREST; every writer
 * retries once without them (isProvenanceDrift / withoutProvenance), so deploy
 * order cannot break an upload, and the retry is logged.
 */

export const SUPPLIED_VIA = Object.freeze({ CLIENT: 'client_portal', OFFICER: 'officer_upload', MIGRATION: 'migration_pack' });
export const PROVENANCE_COLUMNS = Object.freeze(['supplied_via', 'uploaded_by', 'client_confirmed_at']);

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The row with its provenance. uploadedBy is kept only when it is a user id. */
export function withProvenance(row, via, uploadedBy = null) {
  const out = { ...(row || {}), supplied_via: via };
  if (uploadedBy && UUID.test(String(uploadedBy))) out.uploaded_by = String(uploadedBy);
  return out;
}

/** True when a database error is about a provenance column it does not have yet. */
export function isProvenanceDrift(err) {
  const m = String((err && (err.message || err.hint || err.details)) || err || '');
  return /supplied_via|uploaded_by|client_confirmed_at/.test(m);
}

/** The row (or rows) without the provenance columns, for the drift retry. */
export function withoutProvenance(rowOrRows) {
  const strip = (r) => { const o = { ...(r || {}) }; for (const k of PROVENANCE_COLUMNS) delete o[k]; return o; };
  return Array.isArray(rowOrRows) ? rowOrRows.map(strip) : strip(rowOrRows);
}

// ── Is a firm supplied document out of date? ───────────────────────────────
// The reading of a document the firm supplied returns an explicit field
// (api/df-process.js FIRM_SUPPLIED_CURRENTNESS):
//   currentness: { current: true | false, reason: "..." }
// and that decides. Only a reading without it (made before 14/10/2026, or by
// the single document analysis) falls back to its flags and findings, and then
// only a statement that the document itself is too old, expired or not current
// counts: a sentence about a previous, former or old address, name or officer
// inside a current document never does. The analysis never leaves the server:
// callers send only the answer.
const OUT_OF_DATE = [
  /\bout[ -]of[ -]date\b/i, /\boutdated\b/i, /\bexpired\b/i, /\bno longer (?:valid|current)\b/i,
  /\bnot (?:recent|current)\b/i, /\bnot sufficiently recent\b/i, /\bstale\b/i,
  /\b(?:older|more) than (?:three|3|six|6|twelve|12) months\b/i, /\bover (?:three|3|six|6|twelve|12) months old\b/i,
  /\bexceeds? the (?:three|3|six|6|twelve|12)[ -]month\b/i, /\bdated more than\b/i, /\bnot within the last (?:three|3|six|6|twelve|12) months\b/i,
  /\bcurrent (?:copy|version) (?:is )?(?:required|needed)\b/i, /\bmore recent (?:copy|version|statement|document) (?:is )?(?:required|needed)\b/i,
];
// A statement that it is NOT out of date never counts.
const NEGATED = /\b(?:is not|isn't|not) (?:out[ -]of[ -]date|expired|outdated|stale)\b|\bwithin (?:the last )?(?:three|3|six|6|twelve|12) months\b|\bcurrent and valid\b|\bremains? (?:valid|in force|current)\b/i;
// A sentence about something the document mentions, not the document itself.
const ABOUT_SOMETHING_ELSE = /\b(?:previous|former|prior|old|earlier|past)\s+(?:registered\s+)?(?:address|addresses|name|names|director|directors|officer|officers|office|licence|license|passport|employer|shareholder|shareholders)\b/i;

function textsOf(analysis) {
  if (!analysis) return [];
  if (typeof analysis !== 'object') return [String(analysis)];
  const out = [];
  for (const k of ['materialFlags', 'notedFlags', 'informationals', 'keyFindings']) {
    for (const x of Array.isArray(analysis[k]) ? analysis[k] : []) {
      if (typeof x === 'string') out.push(x);
      else if (x && typeof x === 'object') out.push([x.flag, x.text, x.title, x.detail, x.description, x.finding].filter(Boolean).join(' '));
    }
  }
  return out;
}
const asObject = (a) => { if (typeof a === 'string') { try { return JSON.parse(a); } catch (_) { return a; } } return a; };

/** The reading's own verdict: { current, reason, explicit } (explicit false = word match fallback). */
export function currentnessOf(analysis) {
  const a = asObject(analysis);
  const c = a && typeof a === 'object' ? a.currentness : null;
  if (c && typeof c === 'object' && typeof c.current === 'boolean') return { current: c.current, reason: String(c.reason || '').slice(0, 300), explicit: true };
  for (const t of textsOf(a)) {
    for (const sentence of String(t || '').split(/(?<=[.;])\s+/)) {
      if (!sentence) continue;
      if (ABOUT_SOMETHING_ELSE.test(sentence)) continue;
      if (NEGATED.test(sentence)) continue;
      if (OUT_OF_DATE.some(re => re.test(sentence))) return { current: false, reason: sentence.slice(0, 300), explicit: false };
    }
  }
  return { current: true, reason: '', explicit: false };
}

/** True when the document's reading says it is out of date. */
export function needsCurrentCopy(analysis) {
  return currentnessOf(analysis).current === false;
}

// ── The client facing state of a firm supplied document ────────────────────
// confirmed     the client confirmed it is theirs and current
// replaced      the client sent a current copy in its place
// needs_current the analysis found it out of date: the client is asked for a current one
// awaiting      waiting for the client to confirm it
export function suppliedDocState(row, replacements = {}) {
  if (!row || row.supplied_via !== SUPPLIED_VIA.OFFICER) return null;
  if (replacements && row.id != null && replacements[row.id]) return 'replaced';
  if (row.client_confirmed_at) return 'confirmed';
  if (needsCurrentCopy(row.analysis)) return 'needs_current';
  return 'awaiting';
}

const dmy = (v) => { const d = new Date(v); return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }); };

/** The officer facing line for the Documents panel and the Audit Record (no dashes). */
export function provenanceLabel(row, nameOf = () => null) {
  if (!row || !row.supplied_via) return '';
  const on = row.uploaded_at ? ' on ' + dmy(row.uploaded_at) : '';
  if (row.supplied_via === SUPPLIED_VIA.CLIENT) return 'Uploaded by the client through the portal' + on;
  if (row.supplied_via === SUPPLIED_VIA.MIGRATION) return 'From the migration pack' + on;
  if (row.supplied_via === SUPPLIED_VIA.OFFICER) {
    const who = (row.uploaded_by && nameOf(row.uploaded_by)) || 'an officer';
    if (row.internal === true) return `Uploaded by ${who}${on}. Internal, never shown to the client`;
    const tail = row.client_confirmed_at ? `client confirmed current on ${dmy(row.client_confirmed_at)}` : 'awaiting client confirmation';
    return `Supplied to the firm, uploaded by ${who}${on} (${tail})`;
  }
  return '';
}
